// Single source of truth for site navigation — shared by the Header (desktop
// mega-menus + mobile drawer), the Footer and the /explore Directory, and
// editable by admins via Admin → Navigation. Labels are {deva, en}.
//
// The six primary categories express intent:
//   Learn     → understand Dharma
//   Practice  → practise Dharma
//   Explore   → discover Bharat's sacred heritage
//   Guidance  → seek spiritual / life guidance
//   Community → participate and connect
//   Services  → book, request, support or participate
// Items flagged `soon: true` render with a subtle "Coming Soon" badge and link
// to a tasteful placeholder page — never a broken route.

export const NAV = [
  {
    key: "learn",
    label: { deva: "ज्ञान", en: "Learn" },
    blurb: { deva: "धर्म को समझें", en: "Understand Dharma" },
    items: [
      { to: "/kathas", deva: "कथा एवं गाथाएँ", en: "Kathas & Stories" },
      { to: "/rituals", deva: "पूजा विधि", en: "Rituals & Pooja Guides" },
      { to: "/festivals", deva: "पर्व एवं उत्सव", en: "Festivals & Observances" },
      { to: "/scriptures", deva: "शास्त्र", en: "Scriptures" },
      { to: "/granthas", deva: "ग्रंथ (सचित्र पाठ)", en: "Sacred Scriptures" },
      { to: "/books", deva: "ग्रंथ एवं पांडुलिपियाँ", en: "eBooks & Manuscripts" },
      { to: "/philosophy", deva: "धर्म एवं दर्शन", en: "Dharma & Philosophy" },
      { to: "/rishis", deva: "ऋषि एवं आचार्य", en: "Rishis & Acharyas" },
      { to: "/amnaya-peethas", deva: "चार आम्नाय पीठ", en: "Four Amnaya Peethas", hideNav: true },
      { to: "/samskaras", deva: "संस्कार", en: "Samskaras" },
      { to: "/sanskrit", deva: "संस्कृत", en: "Sanskrit", soon: true },
      { to: "/pilgrimage", deva: "तीर्थ", en: "Pilgrimage", hideNav: true },
      { to: "/quiz", deva: "प्रश्नोत्तरी", en: "Dharma Quiz" },
      { to: "/glossary", deva: "शब्दकोश", en: "Glossary" },
    ],
  },
  {
    key: "practice",
    label: { deva: "साधना", en: "Practice" },
    blurb: { deva: "धर्म का आचरण", en: "Practise Dharma" },
    items: [
      { to: "/mantras", deva: "मंत्र", en: "Mantras" },
      { to: "/suktas", deva: "सूक्त", en: "Suktas" },
      { to: "/stotras", deva: "स्तोत्र", en: "Stotras" },
      { to: "/japa", deva: "जप", en: "Japa" },
      { to: "/audios", deva: "ध्वनि संग्रह", en: "Audio Library" },
      { to: "/sadhana", deva: "साधना", en: "Sadhana" },
      { to: "/dainik-pooja", deva: "दैनिक पूजा", en: "Daily Pooja" },
      { to: "/sandhya-vandana", deva: "संध्या वंदना", en: "Sandhya Vandana" },
    ],
  },
  {
    key: "explore",
    label: { deva: "अन्वेषण", en: "Explore" },
    blurb: { deva: "भारत की धरोहर", en: "Discover Bharat's heritage" },
    items: [
      { to: "/explore", deva: "संपूर्ण सूची", en: "Full Directory" },
      { to: "/temples", deva: "भारत के मंदिर", en: "Temples of Bharat" },
      { to: "/deities", deva: "देवी-देवता", en: "Deities" },
      { to: "/calendar", deva: "पर्व-पंचांग", en: "Festival Calendar" },
      { to: "/live-darshan", deva: "लाइव दर्शन", en: "Live Darshan", soon: true },
    ],
  },
  {
    key: "guidance",
    label: { deva: "मार्गदर्शन", en: "Guidance" },
    blurb: { deva: "आध्यात्मिक परामर्श", en: "Seek guidance" },
    items: [
      { to: "/services/astrology", deva: "ज्योतिष", en: "Jyotisha", sub: { deva: "वैदिक ज्योतिष", en: "Vedic Astrology" } },
      { to: "/services/numerology", deva: "अंक ज्योतिष", en: "Numerology" },
      { to: "/services/vastu", deva: "वास्तु", en: "Vastu" },
      { to: "/services/muhurt", deva: "मुहूर्त", en: "Muhurta" },
    ],
  },
  {
    key: "community",
    label: { deva: "समुदाय", en: "Community" },
    blurb: { deva: "सहभागिता एवं संवाद", en: "Participate & connect" },
    items: [
      { to: "/leaderboard", deva: "साधना सूची", en: "Sadhana Leaderboard" },
      { to: "/groups", deva: "समूह एवं मंडली", en: "Groups & Circles" },
      { to: "/events", deva: "आयोजन एवं उत्सव", en: "Events" },
      { to: "/discussions", deva: "चर्चा मंच", en: "Discussions" },
      { to: "/qna", deva: "प्रश्न एवं उत्तर", en: "Questions & Answers" },
      { to: "/invite", deva: "आमंत्रण", en: "Invite & Earn" },
    ],
  },
  {
    key: "services",
    label: { deva: "सेवाएँ", en: "Services" },
    blurb: { deva: "बुकिंग एवं सहयोग", en: "Book, request & support" },
    items: [
      { to: "/services/pooja", deva: "पूजा", en: "Pooja" },
      { to: "/services/homam", deva: "होम / हवन", en: "Homam" },
      { to: "/services/yagya", deva: "यज्ञ", en: "Yagya" },
      { to: "/services", deva: "पूजा बुकिंग", en: "Pooja Booking" },
    ],
  },
  {
    key: "soon",
    label: { deva: "आगामी", en: "Coming Soon" },
    blurb: { deva: "शीघ्र आ रहा है", en: "In development" },
    items: [
      { to: "/premium", deva: "धर्मार्थ प्रीमियम", en: "Dharmartha Premium", soon: true },
      { to: "/courses", deva: "पाठ्यक्रम", en: "Courses", soon: true },
      { to: "/veda-chanting", deva: "वेद पाठ", en: "Chanting the Veda", soon: true },
      { to: "/sankalpa", deva: "संकल्प", en: "Sankalpa", soon: true },
      { to: "/naming", deva: "नामकरण", en: "Baby Names", soon: true },
      { to: "/puja-guidance", deva: "पूजा-अनुष्ठान परामर्श", en: "Pooja & Ritual Guidance", soon: true },
      { to: "/anushthan", deva: "अनुष्ठान", en: "Anushthan", soon: true },
      { to: "/crowdfunding", deva: "मंदिर हेतु सहयोग", en: "Crowdfunding for Temples", soon: true },
    ],
  },
  {
    key: "info",
    label: { deva: "परिचय एवं जानकारी", en: "About & Info" },
    blurb: { deva: "धर्मार्थ के बारे में", en: "About Dharmartha" },
    items: [
      { to: "/about", deva: "परिचय", en: "About Dharmartha" },
      { to: "/support", deva: "सहयोग करें", en: "Support Us", sub: { deva: "धर्मार्थ को दान दें", en: "Donate to Dharmartha" } },
      { to: "/feedback", deva: "संपर्क एवं प्रतिक्रिया", en: "Contact & Feedback" },
      { to: "/collaborate", deva: "सहयोग करें", en: "Collaborate & Work With Us" },
      { to: "/changelog", deva: "नया क्या है", en: "What's New" },
      { to: "/sitemap", deva: "साइट मानचित्र", en: "Sitemap" },
      { to: "/terms", deva: "उपयोग की शर्तें", en: "Terms of Use" },
      { to: "/privacy", deva: "गोपनीयता नीति", en: "Privacy Policy" },
      { to: "/disclaimer", deva: "अस्वीकरण", en: "Disclaimer" },
      { to: "/dpdp", deva: "डीपीडीपी अधिनियम सूचना", en: "DPDP Act Notice" },
    ],
  },
];

// Standalone primary nav item.
export const PATRIKA = {
  to: "/patrika", deva: "पत्रिका", en: "Patrika",
  sub: { deva: "लेख, कथाएँ एवं विचार", en: "Articles, Stories & Insights" },
};

// Standalone primary nav item — Panchangam (kept out of the Explore group so it
// appears on its own in the header and footer).
export const PANCHANGA = {
  to: "/panchang", deva: "पंचांग", en: "Panchangam",
  sub: { deva: "आज का पंचांग", en: "Today's Panchangam" },
};

export const PANCHANGA_PREMIUM = {
  to: "/panchanga-premium", deva: "पंचांग प्रीमियम", en: "Panchangam Premium",
  sub: { deva: "वैयक्तिक ज्योतिष", en: "Personalised jyotish" },
};

// About group (footer + mobile). Kept separate from the six content categories.
export const ABOUT_LINKS = [
  { to: "/about", deva: "परिचय", en: "About Dharmartha" },
  { to: "/feedback", deva: "संपर्क एवं प्रतिक्रिया", en: "Contact & Feedback" },
];

// Account group. Kept separate from content navigation.
export const ACCOUNT_LINKS = [
  { to: "/account", deva: "खाता", en: "Account" },
  { to: "/my-bookings", deva: "मेरी बुकिंग", en: "My Bookings" },
  { to: "/bookmarks", deva: "संचयन", en: "Saved" },
  { to: "/checklists", deva: "पूजा सूची", en: "Pooja Checklists" },
];

// Groups keyed for quick lookup.
export const NAV_BY_KEY = Object.fromEntries(NAV.map((g) => [g.key, g]));

// Global registry of every navigable item across all base groups — lets the
// admin builder move an item from one category to another.
export const NAV_ITEM_BY_TO = Object.fromEntries(NAV.flatMap((g) => g.items).map((it) => [it.to, it]));

export const groupOrder = (cfg = {}) => {
  const order = [];
  (cfg.order || []).forEach((k) => { if (NAV_BY_KEY[k] && !order.includes(k)) order.push(k); });
  NAV.forEach((g) => { if (!order.includes(g.key)) order.push(g.key); });
  return order;
};

// Merge the immutable base NAV with an admin override config for a context
// (header / footer / directory). Honours group order, item order & regrouping,
// per-node hidden flags and label overrides. New groups/items added in code
// appear automatically in their base group even if the saved config predates them.
export function resolveNav(cfg = {}) {
  cfg = cfg || {};
  const referenced = new Set();
  Object.values(cfg.groups || {}).forEach((gc) => (gc.itemsOrder || []).forEach((to) => referenced.add(to)));

  return groupOrder(cfg).map((key) => {
    const base = NAV_BY_KEY[key];
    if (!base) return null;
    const gc = (cfg.groups && cfg.groups[key]) || {};
    if (gc.hidden) return null;

    const itemOrder = [];
    (gc.itemsOrder || base.items.map((it) => it.to)).forEach((to) => {
      if (NAV_ITEM_BY_TO[to] && !itemOrder.includes(to)) itemOrder.push(to);
    });
    // New code-added items in this base group that no config references yet.
    base.items.forEach((it) => { if (!referenced.has(it.to) && !itemOrder.includes(it.to)) itemOrder.push(it.to); });

    const items = itemOrder.map((to) => {
      const bi = NAV_ITEM_BY_TO[to];
      const ic = (gc.items && gc.items[to]) || {};
      if (ic.hidden) return null;
      return {
        ...bi,
        deva: (ic.label && (ic.label.hi || ic.label.deva)) || bi.deva,
        en: (ic.label && ic.label.en) || bi.en,
      };
    }).filter(Boolean);

    return {
      ...base,
      label: {
        deva: (gc.label && (gc.label.hi || gc.label.deva)) || base.label.deva,
        en: (gc.label && gc.label.en) || base.label.en,
      },
      items,
    };
  }).filter(Boolean);
}

